/** Online Booking — mock data (spec 50 §4). Names and times are the spec's, exactly. */
import type {
  BookingFilter,
  BookingListStatus,
  BookingNumbers,
  BookingPageConfig,
  BookingRequestRow,
  BookingSettings,
  BookingStatusMeta,
} from './types';

/* The public booking page — one worked example (Maya Okafor). */
export const BOOKING_PAGE: BookingPageConfig = {
  slug: 'alamo-heights',
  practiceName: 'Alamo Heights Dental',
  practiceInitials: 'AH',
  practiceColor: '#1D6F6A',
  practicePhone: '(210) 555-0147',
  callbackPhone: '(210) 555-0182',
  feeAmount: 50,
  feeWho: 'new_only',
  patientName: 'Maya Okafor',
  firstAvailable: 'Tue 6 Oct, 9:00 am',
  providerLine: 'With Dr. Carter',
  reasons: [
    { id: 'npexam', name: 'New patient exam and cleaning', description: 'A full check-up, x-rays and a cleaning.', minutes: 60, forNew: true },
    { id: 'cleaning', name: 'Cleaning', description: 'A routine hygiene visit.', minutes: 60, forNew: false },
    { id: 'checkup', name: 'Check-up', description: 'An exam with your dentist.', minutes: 30, forNew: false },
    { id: 'child', name: "Child's first visit", description: 'A gentle first visit for children.', minutes: 45, forNew: true },
    { id: 'toothache', name: 'Tooth pain', description: 'Something is hurting and you need to be seen.', minutes: 30, forNew: true },
    { id: 'whitening', name: 'Whitening consult', description: 'Talk through your options for a brighter smile.', minutes: 30, forNew: true },
    { id: 'crown', name: 'Crown or filling', description: 'Repair work on a tooth.', minutes: 60, forNew: false },
    { id: 'consult', name: 'Second opinion', description: 'Bring a plan from another office to review.', minutes: 30, forNew: true },
  ],
  days: [
    { id: 'd1', label: 'Tue 6 Oct', times: ['9:00 am', '9:30 am', '11:00 am', '2:00 pm', '3:30 pm'] },
    { id: 'd2', label: 'Wed 7 Oct', times: ['8:30 am', '10:00 am', '1:00 pm', '4:00 pm'] },
    { id: 'd3', label: 'Thu 8 Oct', times: ['9:00 am', '11:00 am', '11:30 am', '2:30 pm'] },
    { id: 'd4', label: 'Fri 9 Oct', times: ['10:00 am', '1:30 pm', '3:30 pm'] },
    { id: 'd5', label: 'Mon 12 Oct', times: ['8:00 am', '10:00 am', '2:00 pm'] },
  ],
  planSummary: {
    carrier: 'Delta Dental',
    network: 'in',
    plan: 'PPO · Group 00417',
    member: 'Maya Okafor · ID DD 4471 20938',
    heldBy: 'You',
    status: 'Active · checked today',
    thisVisit: 'New patient exam and x-rays: usually covered at 100%',
  },
};

/* Staff list — the seven rows (spec §4). */
export const BOOKING_REQUESTS: BookingRequestRow[] = [
  {
    id: 'r1',
    patientName: 'Maya Okafor',
    patientKind: 'new',
    reason: 'New patient exam and cleaning',
    visitWhen: 'Tue 6 Oct, 9:00 am · Dr. Carter',
    requested: 'Today 9:12 pm',
    call: 'Ringing',
    insurance: 'Delta Dental · in network',
    fee: '—',
    status: 'calling',
  },
  {
    id: 'r2',
    patientName: 'Darius Whitfield',
    patientKind: 'new',
    reason: 'New patient exam and cleaning',
    visitWhen: 'Wed 7 Oct, 2:00 pm · Dr. Carter',
    requested: 'Today 8:40 pm',
    call: 'Answered · 2:05',
    insurance: 'Guardian · out of network',
    fee: 'Waiting',
    status: 'waiting_fee',
  },
  {
    id: 'r3',
    patientName: 'Priya Raman',
    patientKind: 'returning',
    reason: 'Cleaning',
    visitWhen: 'Thu 8 Oct, 11:00 am · Dr. Carter',
    requested: 'Today 7:05 pm',
    call: 'No call',
    insurance: 'On file',
    fee: '—',
    status: 'booked',
  },
  {
    id: 'r4',
    patientName: 'Leo Okafor',
    patientKind: 'new',
    bookedForLine: 'for Leo Okafor, booked by Maya Okafor',
    reason: "Child's first visit",
    visitWhen: 'Fri 9 Oct, 3:30 pm · Dr. Carter',
    requested: 'Yesterday 6:22 pm',
    call: 'Answered · 1:48',
    insurance: 'None',
    fee: 'Paid $50',
    status: 'booked',
  },
  {
    id: 'r5',
    patientName: 'J. Smith',
    patientKind: 'new',
    reason: 'New patient exam and cleaning',
    visitWhen: 'Tue 6 Oct, 1:00 pm · Dr. Carter',
    requested: 'Yesterday 3:11 pm',
    call: 'No answer · 2 tries',
    insurance: 'None',
    fee: '—',
    status: 'no_answer',
  },
  {
    id: 'r6',
    patientName: 'Tessa Lindqvist',
    patientKind: 'new',
    reason: 'Whitening consult',
    visitWhen: 'Mon 12 Oct, 10:00 am · Dr. Carter',
    requested: '2 days ago',
    call: 'Answered · 0:41',
    insurance: "Couldn't be checked",
    fee: '—',
    status: 'not_verified',
  },
  {
    id: 'r7',
    patientName: 'Hank Morales',
    patientKind: 'returning',
    reason: 'Cleaning',
    visitWhen: 'Wed 14 Oct, 8:00 am · Dr. Carter',
    requested: '2 days ago',
    call: 'Answered · 1:20',
    insurance: 'On file',
    fee: '—',
    status: 'fee_not_paid',
  },
];

export const BOOKING_NUMBERS: BookingNumbers = {
  requests: 46,
  reachedPct: 83,
  booked: 31,
  showedPct: 96,
};

/** Status → label, tone, and the one link the row may carry. */
export const BOOKING_STATUS_META: Record<BookingListStatus, BookingStatusMeta> = {
  calling: { label: 'ELVA is calling', tone: 'blue', link: 'none' },
  waiting_fee: { label: 'Waiting for the fee', tone: 'amber', link: 'none' },
  booked: { label: 'Booked', tone: 'green', link: 'appointment' },
  no_answer: { label: 'No answer', tone: 'grey', link: 'triage' },
  not_verified: { label: 'Not verified', tone: 'grey', link: 'triage' },
  cancelled_call: { label: 'Cancelled on the call', tone: 'grey', link: 'triage' },
  fee_not_paid: { label: 'Fee not paid', tone: 'grey', link: 'triage' },
};

/** Which statuses a filter chip keeps. */
export function bookingFilterMatch(filter: BookingFilter, status: BookingListStatus): boolean {
  if (filter === 'all') return true;
  if (filter === 'booked') return status === 'booked';
  if (filter === 'in_progress') return status === 'calling' || status === 'waiting_fee';
  // not_booked
  return status === 'no_answer' || status === 'not_verified' || status === 'cancelled_call' || status === 'fee_not_paid';
}

/** The settings page state (spec §2) — Alamo Heights: fee on, $50, new patients only. */
export const BOOKING_SETTINGS: BookingSettings = {
  on: true,
  bookingUrl: 'book.elva.ai/alamo-heights',
  reasonsOnlineCount: 12,
  hasCallDisclosure: true,
  hasAnyoneReason: true,
  hasElvaPay: true,
  timesPerDay: 4,
  earliest: 'next_day',
  furthest: '60',
  feeOn: true,
  feeAmount: 50,
  feeWho: 'new_only',
  callReturning: false,
  bought: true,
};

export const BOOKING_EARLIEST_OPTIONS = [
  { value: '2h', label: '2 hours' },
  { value: '4h', label: '4 hours' },
  { value: 'next_day', label: 'The next day' },
  { value: '2d', label: '2 days' },
];

export const BOOKING_FURTHEST_OPTIONS = [
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
];
